/**
 * Webtech HR Engine™ - My Skills Space (Employee Space)
 * Page espace personnel pour les employés connectés
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Brain,
  Target,
  TrendingUp,
  Award,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronRight,
  Calendar,
  Play,
  User,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

export default function MyEvaluationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  const userId = user?.id;
  
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [notLinked, setNotLinked] = useState(false);
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [expiredEvaluations, setExpiredEvaluations] = useState([]);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [developmentPlans, setDevelopmentPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('evaluations');

  useEffect(() => {
    if (companyId && userId) {
      loadMyEmployee();
    }
  }, [companyId, userId]);

  const loadMyEmployee = async () => {
    try {
      setLoading(true);
      
      // First, get the employee linked to this user
      const empRes = await api.get(`/employee-accounts/my-employee?user_id=${userId}&company_id=${companyId}`);
      
      if (!empRes.data.found) {
        setNotLinked(true);
        setLoading(false);
        return;
      }
      
      setEmployee(empRes.data.employee);
      const employeeId = empRes.data.employee.id;
      
      // Load employee data
      await loadEmployeeData(employeeId);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
      setNotLinked(true);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeData = async (employeeId) => {
    try {
      // Load objectives for self-evaluation (new system) AND performance evaluations
      const [objectivesRes, performanceEvalsRes, gapRes, plansRes] = await Promise.all([
        api.get(`/objectives`).catch(() => ({ data: { objectives: [] } })),
        api.get(`/performance-eval/my-evaluations?employee_id=${employeeId}`).catch(() => ({ data: { evaluations: [] } })),
        api.get(`/skills/gap-analysis/${employeeId}?company_id=${companyId}`).catch(() => ({ data: { hasJobProfile: false } })),
        api.get(`/skills-intelligence/upskilling-plans?company_id=${companyId}&employee_id=${employeeId}`).catch(() => ({ data: { plans: [] } }))
      ]);
      
      // Get objectives that need self-evaluation from OLD system
      const allObjectives = objectivesRes.data.objectives || [];
      
      // Filter objectives for this employee that need self-evaluation (exclude expired and rejected)
      const myObjectives = allObjectives.filter(obj => 
        obj.employeeId === employeeId && 
        obj.status !== 'rejected' &&
        obj.status !== 'expired' &&
        !obj.selfEvaluation // No self-evaluation yet
      );
      
      // Transform OLD objectives to pending evaluations format
      const pendingFromOldSystem = myObjectives.map(obj => ({
        id: obj.id,
        type: 'self',
        title: obj.title,
        description: obj.description,
        weight: obj.weight,
        objectiveType: obj.type || obj.objectiveType,
        status: obj.status,
        deadline: obj.endDate,
        source: 'objectives'
      }));
      
      // Get PERFORMANCE evaluations (new system)
      const performanceEvals = performanceEvalsRes.data?.evaluations || [];
      const pendingFromPerformance = performanceEvals
        .filter(e => e.status === 'self_eval_pending')
        .flatMap(evaluation => {
          // Each evaluation can have multiple objectives
          return (evaluation.objectives || []).map((obj, idx) => ({
            id: `${evaluation.id}_obj_${idx}`,
            evaluationId: evaluation.id,
            objectiveIndex: idx,
            type: 'self',
            title: obj.title,
            description: obj.description,
            weight: obj.weight,
            selfScore: obj.selfScore,
            selfComment: obj.selfComment,
            status: obj.selfScore ? 'done' : 'pending',
            campaignId: evaluation.campaignId,
            campaignName: evaluation.campaignName,
            deadline: evaluation.selfEvalEndDate,
            source: 'performance_eval'
          }));
        });
      
      // Waiting for manager evaluation (performance system)
      const waitingManagerFromPerformance = performanceEvals
        .filter(e => e.status === 'manager_eval_pending')
        .flatMap(evaluation => {
          return (evaluation.objectives || []).map((obj, idx) => ({
            id: `${evaluation.id}_obj_${idx}`,
            evaluationId: evaluation.id,
            type: 'waiting_manager',
            title: obj.title,
            description: obj.description,
            weight: obj.weight,
            selfScore: obj.selfScore,
            status: 'waiting',
            source: 'performance_eval'
          }));
        });
      
      // Waiting for N2/RH validation (performance system)
      const waitingN2FromPerformance = performanceEvals
        .filter(e => e.status === 'n2_validation_pending' || e.status === 'manager_eval_done')
        .flatMap(evaluation => {
          return (evaluation.objectives || []).map((obj, idx) => ({
            id: `${evaluation.id}_obj_${idx}`,
            evaluationId: evaluation.id,
            type: 'waiting_n2',
            title: obj.title,
            description: obj.description,
            weight: obj.weight,
            selfScore: obj.selfScore,
            managerScore: obj.managerScore,
            status: 'waiting_n2',
            source: 'performance_eval'
          }));
        });
      
      // Completed evaluations (performance system)
      const completedFromPerformance = performanceEvals
        .filter(e => e.status === 'completed' || e.status === 'validated')
        .flatMap(evaluation => {
          return (evaluation.objectives || []).map((obj, idx) => ({
            id: `${evaluation.id}_obj_${idx}`,
            evaluationId: evaluation.id,
            type: 'completed',
            title: obj.title,
            selfScore: obj.selfScore,
            managerScore: obj.managerScore,
            weight: obj.weight,
            source: 'performance_eval'
          }));
        });
      
      // Get expired objectives from OLD system
      const expiredObjectives = allObjectives.filter(obj => 
        obj.employeeId === employeeId && 
        obj.status === 'expired'
      ).map(obj => ({
        id: obj.id,
        type: 'expired',
        title: obj.title,
        description: obj.description,
        expiredReason: obj.expiredReason || 'Campagne terminée sans auto-évaluation',
        expiredAt: obj.expiredAt
      }));
      
      setExpiredEvaluations(expiredObjectives);
      
      // Also include objectives waiting for manager evaluation from OLD system
      const waitingManager = allObjectives.filter(obj => 
        obj.employeeId === employeeId && 
        obj.selfEvaluation && 
        !obj.managerEvaluation
      ).map(obj => ({
        id: obj.id,
        type: 'waiting_manager',
        title: obj.title,
        description: obj.description,
        weight: obj.weight,
        selfScore: obj.selfEvaluation?.score,
        status: 'waiting',
        source: 'objectives'
      }));
      
      // Completed evaluations from OLD system
      const completed = allObjectives.filter(obj => 
        obj.employeeId === employeeId && 
        obj.selfEvaluation && 
        obj.managerEvaluation
      ).map(obj => ({
        id: obj.id,
        type: 'completed',
        title: obj.title,
        selfScore: obj.selfEvaluation?.score,
        managerScore: obj.managerEvaluation?.score,
        weight: obj.weight,
        source: 'objectives'
      }));
      
      // Combine all sources
      setPendingEvaluations([
        ...pendingFromPerformance,
        ...pendingFromOldSystem, 
        ...waitingManagerFromPerformance,
        ...waitingN2FromPerformance,
        ...waitingManager, 
        ...completedFromPerformance,
        ...completed
      ]);
      setGapAnalysis(gapRes.data);
      setDevelopmentPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Error loading employee data:', error);
    }
  };

  const getReadinessColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Handle marking an action as done (pending manager validation)
  const handleMarkActionDone = async (planId, actionIndex) => {
    try {
      // Update the action status to pending_validation
      await api.put(`/skills-intelligence/upskilling-plans/${planId}/action/${actionIndex}`, {
        status: 'pending_validation',
        completedAt: new Date().toISOString(),
        completedBy: 'employee'
      });
      
      toast.success('Action marquée comme terminée ! En attente de validation du manager.');
      
      // Reload data
      if (employee?.id) {
        await loadEmployeeData(employee.id);
      }
    } catch (error) {
      console.error('Error marking action:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Mes évaluations">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  // If user is not linked to an employee
  if (notLinked) {
    return (
      <DashboardLayout title="Mes évaluations">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-amber-500" />
              Mes évaluations
            </h1>
            <p className="text-slate-500 mt-1">Complétez vos évaluations et suivez votre progression</p>
          </div>

          <Card className="py-12 border-yellow-200 bg-yellow-50">
            <CardContent className="text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Compte non lié</h3>
              <p className="text-slate-600 max-w-md mx-auto mb-4">
                Votre compte utilisateur n'est pas encore lié à votre fiche employé. 
                Contactez votre responsable RH pour activer votre accès.
              </p>
              <div className="bg-white rounded-lg p-4 max-w-sm mx-auto border">
                <p className="text-sm text-slate-500">Connecté en tant que :</p>
                <p className="font-medium text-slate-900">{user?.email}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const selfPending = pendingEvaluations.filter(e => e.type === 'self');
  const waitingManager = pendingEvaluations.filter(e => e.type === 'waiting_manager');
  const waitingN2 = pendingEvaluations.filter(e => e.type === 'waiting_n2');
  const completedEvals = pendingEvaluations.filter(e => e.type === 'completed');
  const managerPending = pendingEvaluations.filter(e => e.type === 'manager');
  const activePlans = developmentPlans.filter(p => p.status === 'active');

  return (
    <DashboardLayout title="Mes évaluations">
      <div className="space-y-6">
        {/* Header with employee info */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-amber-500" />
              Mes évaluations
            </h1>
            <p className="text-slate-500 mt-1">Complétez vos évaluations et suivez votre progression</p>
          </div>
          
          {employee && (
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-webtech-blue flex items-center justify-center text-white font-medium">
                  {employee.firstName?.[0]}{employee.lastName?.[0]}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{employee.firstName} {employee.lastName}</p>
                  <p className="text-sm text-slate-500">{employee.jobTitle || employee.position || 'Employé'}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={selfPending.length > 0 ? 'border-yellow-200 bg-yellow-50' : ''}>
            <CardContent className="pt-6">
              <div className="text-center">
                <Clock className={`h-6 w-6 mx-auto mb-2 ${selfPending.length > 0 ? 'text-yellow-500' : 'text-slate-400'}`} />
                <div className={`text-2xl font-bold ${selfPending.length > 0 ? 'text-yellow-600' : 'text-slate-900'}`}>
                  {selfPending.length}
                </div>
                <p className="text-sm text-slate-500">Évaluations en attente</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                {gapAnalysis?.hasJobProfile ? (
                  <>
                    <Target className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                    <div className={`text-2xl font-bold ${getReadinessColor(gapAnalysis?.summary?.readinessScore || 0)}`}>
                      {gapAnalysis?.summary?.readinessScore || 0}%
                    </div>
                    <p className="text-sm text-slate-500">Score de préparation</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-6 w-6 mx-auto text-slate-400 mb-2" />
                    <div className="text-lg font-medium text-slate-500">-</div>
                    <p className="text-sm text-slate-500">Pas de profil</p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <TrendingUp className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-green-600">{activePlans.length}</div>
                <p className="text-sm text-slate-500">Plans actifs</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Award className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                <div className="text-2xl font-bold text-purple-600">
                  {(gapAnalysis?.summary?.skillsMet || 0) + (gapAnalysis?.summary?.skillsAbove || 0)}
                </div>
                <p className="text-sm text-slate-500">Compétences validées</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="evaluations">
              Évaluations
              {selfPending.length > 0 && (
                <Badge className="ml-2 bg-yellow-100 text-yellow-700">{selfPending.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile">Mon profil</TabsTrigger>
            <TabsTrigger value="plans">Mes plans ({activePlans.length})</TabsTrigger>
          </TabsList>
          
          {/* Pending Evaluations Tab */}
          <TabsContent value="evaluations" className="mt-4">
            {selfPending.length === 0 && waitingManager.length === 0 && waitingN2.length === 0 && completedEvals.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <AlertCircle className="h-16 w-16 mx-auto text-slate-400 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun objectif à évaluer</h3>
                  <p className="text-slate-500">Vous n'avez pas encore d'objectifs assignés pour l'évaluation.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Self evaluations needed */}
                {selfPending.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Target className="h-5 w-5 text-yellow-500" />
                      Mes objectifs à auto-évaluer ({selfPending.length})
                    </h3>
                    <div className="space-y-3">
                      {selfPending.map((evaluation) => (
                        <Card 
                          key={evaluation.id} 
                          className={`hover:shadow-md transition-shadow border-l-4 ${
                            evaluation.selfScore ? 'border-l-green-500 bg-green-50/30' : 'border-l-yellow-500'
                          }`}
                        >
                          <CardContent className="py-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-4 flex-1">
                                <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  evaluation.selfScore ? 'bg-green-100' : 'bg-yellow-100'
                                }`}>
                                  {evaluation.selfScore ? (
                                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                                  ) : (
                                    <Target className="h-6 w-6 text-yellow-600" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-slate-900">{evaluation.title}</h4>
                                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{evaluation.description}</p>
                                  <div className="flex items-center flex-wrap gap-3 mt-2">
                                    <Badge variant="outline" className="text-xs">
                                      Poids: {evaluation.weight}%
                                    </Badge>
                                    {evaluation.selfScore && (
                                      <Badge className="bg-green-100 text-green-700 text-xs">
                                        Score: {evaluation.selfScore}%
                                      </Badge>
                                    )}
                                    {evaluation.deadline && (
                                      <span className="flex items-center gap-1 text-xs text-slate-500">
                                        <Calendar className="h-3 w-3" />
                                        Échéance: {new Date(evaluation.deadline).toLocaleDateString('fr-FR')}
                                      </span>
                                    )}
                                    {evaluation.campaignName && (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                                        {evaluation.campaignName}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {evaluation.selfScore ? (
                                <Button 
                                  variant="outline"
                                  onClick={() => navigate(`/my-evaluations/self/${evaluation.id}`)}
                                  className="border-green-300 text-green-700 hover:bg-green-50"
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Modifier
                                </Button>
                              ) : (
                                <Button 
                                  onClick={() => navigate(`/my-evaluations/self/${evaluation.id}`)}
                                  className="bg-yellow-500 hover:bg-yellow-600"
                                >
                                  <Play className="h-4 w-4 mr-2" />
                                  M'auto-évaluer
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Waiting for manager evaluation */}
                {waitingManager.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-500" />
                      En attente de mon manager ({waitingManager.length})
                    </h3>
                    <div className="space-y-3">
                      {waitingManager.map((evaluation) => (
                        <Card key={evaluation.id} className="border-l-4 border-l-blue-300 bg-blue-50/50">
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                                  <Clock className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-slate-900">{evaluation.title}</h4>
                                  <p className="text-sm text-slate-500">
                                    Votre auto-évaluation: <span className="font-semibold text-blue-600">{evaluation.selfScore}%</span>
                                  </p>
                                </div>
                              </div>
                              <Badge className="bg-blue-100 text-blue-700">
                                En attente du manager
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Waiting for N2/RH validation */}
                {waitingN2.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-purple-500" />
                      En attente de validation RH ({waitingN2.length})
                    </h3>
                    <div className="space-y-3">
                      {waitingN2.map((evaluation) => (
                        <Card key={evaluation.id} className="border-l-4 border-l-purple-300 bg-purple-50/50">
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                                  <User className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-slate-900">{evaluation.title}</h4>
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-sm text-slate-500">
                                      Vous: <span className="font-semibold text-blue-600">{evaluation.selfScore}%</span>
                                    </span>
                                    <span className="text-sm text-slate-500">
                                      Manager: <span className="font-semibold text-green-600">{evaluation.managerScore}%</span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <Badge className="bg-purple-100 text-purple-700">
                                Validation RH en cours
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Completed evaluations */}
                {completedEvals.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      Évaluations terminées ({completedEvals.length})
                    </h3>
                    <div className="space-y-3">
                      {completedEvals.map((evaluation) => (
                        <Card key={evaluation.id} className="border-l-4 border-l-green-400 bg-green-50/50">
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-slate-900">{evaluation.title}</h4>
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-sm text-slate-500">
                                      Vous: <span className="font-semibold">{evaluation.selfScore}%</span>
                                    </span>
                                    <span className="text-sm text-slate-500">
                                      Manager: <span className="font-semibold">{evaluation.managerScore}%</span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <Button variant="outline" size="sm">
                                Voir détails
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Expired evaluations - Campaign closed */}
                {expiredEvaluations.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-500 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-slate-400" />
                      Non évalués - Campagne terminée ({expiredEvaluations.length})
                    </h3>
                    <div className="space-y-3">
                      {expiredEvaluations.map((evaluation) => (
                        <Card key={evaluation.id} className="border-l-4 border-l-slate-300 bg-slate-50/50 opacity-75">
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-lg bg-slate-200 flex items-center justify-center">
                                  <XCircle className="h-6 w-6 text-slate-400" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-slate-600">{evaluation.title}</h4>
                                  <p className="text-sm text-slate-400">{evaluation.expiredReason}</p>
                                </div>
                              </div>
                              <Badge className="bg-slate-200 text-slate-500">
                                Expiré
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-4">
            {!gapAnalysis?.hasJobProfile ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <AlertCircle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun profil de poste assigné</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Votre responsable RH doit vous assigner un profil de poste pour que vous puissiez voir votre analyse de compétences.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Résumé de mes compétences</CardTitle>
                    <CardDescription>Profil de poste: {gapAnalysis.jobProfile?.name}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <div className="text-2xl font-bold text-slate-900">{gapAnalysis.summary?.totalSkills || 0}</div>
                        <p className="text-sm text-slate-500">Compétences requises</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{(gapAnalysis.summary?.skillsMet || 0) + (gapAnalysis.summary?.skillsAbove || 0)}</div>
                        <p className="text-sm text-slate-500">Atteintes</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">{gapAnalysis.summary?.skillsWithGap || 0}</div>
                        <p className="text-sm text-slate-500">À développer</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{gapAnalysis.summary?.criticalGaps || 0}</div>
                        <p className="text-sm text-slate-500">Critiques</p>
                      </div>
                    </div>
                    
                    {/* Readiness Progress */}
                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Score de préparation au poste</span>
                        <span className={`text-sm font-bold ${getReadinessColor(gapAnalysis.summary?.readinessScore || 0)}`}>
                          {gapAnalysis.summary?.readinessScore || 0}%
                        </span>
                      </div>
                      <Progress value={gapAnalysis.summary?.readinessScore || 0} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
                
                {/* Skills with gaps */}
                {gapAnalysis.gaps?.filter(g => g.status === 'gap').length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                        Compétences à développer
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {gapAnalysis.gaps?.filter(g => g.status === 'gap').map(gap => (
                          <div key={gap.skillId} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{gap.skillName}</p>
                              <p className="text-sm text-slate-500">
                                Niveau actuel: {gap.currentLevel || 0} → Requis: {gap.requiredLevel}
                              </p>
                            </div>
                            <Badge className={gap.importance === 'critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                              {gap.importance === 'critical' ? 'Critique' : gap.importance === 'medium' ? 'Moyen' : 'Faible'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Strong skills */}
                {gapAnalysis.gaps?.filter(g => g.status !== 'gap').length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                        Mes points forts
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {gapAnalysis.gaps?.filter(g => g.status !== 'gap').map(gap => (
                          <div key={gap.skillId} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{gap.skillName}</p>
                              <p className="text-sm text-slate-500">
                                Niveau: {gap.currentLevel || 0} / Requis: {gap.requiredLevel}
                              </p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">
                              {gap.status === 'above' ? 'Dépasse' : 'Atteint'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Development Plans Tab */}
          <TabsContent value="plans" className="mt-4">
            {activePlans.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <TrendingUp className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun plan de développement</h3>
                  <p className="text-slate-500">Votre responsable peut créer des plans pour vous aider à progresser.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {activePlans.map(plan => {
                  const completedActions = plan.actions?.filter(a => a.status === 'completed').length || 0;
                  const totalActions = plan.actions?.length || 0;
                  const calculatedProgress = totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;
                  
                  return (
                    <Card key={plan.id} className="hover:shadow-md transition-shadow border-l-4 border-l-purple-500">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                              <Target className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900">{plan.skillName || plan.skill?.name || 'Plan de développement'}</h4>
                              <p className="text-sm text-slate-500">
                                Objectif: Niveau {plan.currentLevel || '?'} → {plan.targetLevel || '?'}
                              </p>
                              {plan.deadline && (
                                <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                                  <Calendar className="h-3 w-3" />
                                  Échéance: {new Date(plan.deadline).toLocaleDateString('fr-FR')}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-bold text-slate-900">{calculatedProgress}%</div>
                            <p className="text-xs text-slate-500">{completedActions}/{totalActions} actions</p>
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <Progress value={calculatedProgress} className="h-2 mb-4" />
                        
                        {/* Actions avec boutons */}
                        {plan.actions && plan.actions.length > 0 && (
                          <div className="border-t pt-4">
                            <p className="text-sm font-medium text-slate-700 mb-3">Mes actions à réaliser :</p>
                            <div className="space-y-3">
                              {plan.actions.map((action, idx) => (
                                <div 
                                  key={idx} 
                                  className={`p-3 rounded-lg border ${
                                    action.status === 'completed' 
                                      ? 'bg-green-50 border-green-200' 
                                      : action.status === 'pending_validation'
                                      ? 'bg-amber-50 border-amber-200'
                                      : 'bg-slate-50 border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 flex-1">
                                      <div className={`mt-0.5 h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        action.status === 'completed' 
                                          ? 'bg-green-500 text-white' 
                                          : action.status === 'pending_validation'
                                          ? 'bg-amber-500 text-white'
                                          : 'bg-slate-200 text-slate-600'
                                      }`}>
                                        {action.status === 'completed' ? (
                                          <CheckCircle2 className="h-4 w-4" />
                                        ) : action.status === 'pending_validation' ? (
                                          <Clock className="h-4 w-4" />
                                        ) : (
                                          <span className="text-xs font-medium">{idx + 1}</span>
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`font-medium ${
                                          action.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'
                                        }`}>
                                          {action.title}
                                        </p>
                                        {action.description && (
                                          <p className="text-sm text-slate-500 mt-0.5">{action.description}</p>
                                        )}
                                        {action.deadline && (
                                          <p className="text-xs text-slate-400 mt-1">
                                            Échéance: {new Date(action.deadline).toLocaleDateString('fr-FR')}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Action buttons */}
                                    <div className="flex-shrink-0">
                                      {action.status === 'completed' ? (
                                        <Badge className="bg-green-100 text-green-700">Validé</Badge>
                                      ) : action.status === 'pending_validation' ? (
                                        <Badge className="bg-amber-100 text-amber-700">En attente validation</Badge>
                                      ) : (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => handleMarkActionDone(plan.id, idx)}
                                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                        >
                                          <CheckCircle2 className="h-4 w-4 mr-1" />
                                          J&apos;ai terminé
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Notes du manager */}
                        {plan.notes && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-xs font-medium text-blue-700 mb-1">Note de votre manager :</p>
                            <p className="text-sm text-blue-900">{plan.notes}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
